/**
 * Calculated-field persistence helpers.
 *
 * The backend only preserves a fixed set of `items` keys: header, footer,
 * background, and per-block layout IDs. To make calc fields work on documents
 * without a header, they're stored in `background` (always present). Reads
 * also accept the legacy header location and a top-level fallback.
 */
import { JSONParse } from "utils/helpers";

export const toItemObject = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value === "string" && value) {
    const parsed = JSONParse(value);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) return parsed;
  }
  return null;
};

/**
 * Reads the calculated-field array from a document's items dict.
 *
 * @param {Object} items - The `layoutData.items` object (or equivalent)
 * @param {Array} [topLevelFallback] - Optional `layoutData.calculatedFields` fallback
 * @returns {Array} calculated fields array (empty when none found)
 */
export function readCalcFieldsFromItems(items, topLevelFallback) {
  if (items) {
    const bgObj = toItemObject(items.background);
    if (bgObj && Array.isArray(bgObj.calculatedFields) && bgObj.calculatedFields.length > 0) {
      return bgObj.calculatedFields;
    }

    const headerObj = toItemObject(items.header);
    if (headerObj && Array.isArray(headerObj.calculatedFields) && headerObj.calculatedFields.length > 0) {
      return headerObj.calculatedFields;
    }
  }

  if (Array.isArray(topLevelFallback) && topLevelFallback.length > 0) {
    return topLevelFallback;
  }

  return [];
}

/**
 * Builds the next `background` value with `calculatedFields` injected.
 * The returned value is the OBJECT form (not stringified) so callers can
 * decide whether to JSON.stringify it before storing.
 *
 * Existing background data (image config, print settings) is preserved.
 *
 * @param {string|Object|undefined} currentBackground
 * @param {Array} calcFields
 * @returns {Object}
 */
export function injectCalcFieldsIntoBackground(currentBackground, calcFields) {
  const base = toItemObject(currentBackground) || {};
  return { ...base, calculatedFields: Array.isArray(calcFields) ? calcFields : [] };
}
